<template>
  <v-form
    ref="lldDetail"
    v-model="isValidationOk"
    lazy-validation>
    <v-container
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        wrap>
        <v-flex
          xs6
          lg3
          md3>
          <v-text-field
            v-model.number="pipeLength"
            :label="`${ $t('Line Length') + ' (' + [ options.levelUnits[systemSettings.levelUnit || 0].desc ] + ')' }`"
            :rules="rules.required.concat(rules.pipeLength)"
            data-testId="text-field-Line-Length"
            class="dover-input"
            @change="onLineLengthChanged()" />
        </v-flex>
        <v-flex
          xs6
          lg3
          md3>
          <v-combobox
            v-model.number="pipeVolumeGallons"
            :label="$t('Pipeline (gal/ft)')"
            :items="pipelineVolumes"
            :rules="rules.required.concat(rules.pipeVolume)"
            data-testId="combobox-Pipeline"
            item-text="name"
            item-value="value"
            return-object
            class="dover-input"
            @click="pipeVolumeGallons = 0"
            @input="onSegmentVolumeChangedGallons"
            @change="onSegmentVolumeChangedGallons"/>
        </v-flex>
        <v-flex
          xs6
          lg3
          md3>
          <v-text-field
            v-model.number="pipeVolume"
            :label="`${ $t('Volume/Length') + ' (' + [ options.volumeUnits[systemSettings.volumeUnit || 0].desc ] + '/' + [ options.levelUnits[systemSettings.levelUnit || 0].desc ] + ')' }`"
            :rules="rules.required.concat(rules.pipeVolume)"
            data-testId="text-field-Volume-Length"
            class="dover-input"
            @change="onSegmentVolumeChanged" />
        </v-flex>
      </v-layout>
    </v-container>
    <v-container
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        wrap>
        <v-flex
          xs6
          lg3
          md3>
          <v-text-field
            v-model.number="testPressure"
            :counter="5"
            :rules="rules.required.concat(rules.span)"
            :label="`${ $t('Minimum Test Pressure (psi)')}`"
            data-testId="text-field-Minimum-Test-Pressure"
            class="dover-input"
            @change="onMinimumTestPressureChanged()" />
        </v-flex>
        <v-flex
          xs6
          lg3
          md3>
          <v-text-field
            v-model.number="dispensingPressure"
            :counter="5"
            :rules="rules.required.concat(rules.span)"
            :label="`${ $t('Minimum Dispensing Pressure (psi)')}`"
            data-testId="text-field-Minimum-Dispensing-Pressure"
            class="dover-input"
            @change="onMinimumDispensingPressureChanged()" />
        </v-flex>
      </v-layout>
    </v-container>
    <v-container
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        wrap>
        <MaglinkComponentDialogYesNo
          :key="dialogYN.key"
          :component-key="dialogYN.componentKey"
          :dialog="dialogYN.show"
          :title="dialogYN.title"
          :label-text="dialogYN.labelText"
          :custom-par1="dialogYN.action"
          :custom-par2="dialogYN.result"
          :custom-par3="dialogYN.sensor"
          data-testId="MaglinkComponentDialogYesNo-dialogYN"
          @dialogResultYesNo="onDialogResult"/>
        <v-flex
          xs6
          lg3
          md3>
          <v-text-field
            v-model.number="lld.lineBeta"
            :counter="5"
            :rules="rules.required.concat(rules.range)"
            :label="`${ $t('Beta') }`"
            :disabled="lld.lineCalibratedBeta > 0.0"
            data-testId="text-field-beta"
            mask="#####"
            class="dover-input"
            @change="onSelectionUpdate()" />
        </v-flex>
        <v-flex
          xs3
          lg3
          md3>
          <v-text-field
            v-model.number="lld.lineCalibratedBeta"
            :label="`${ $t('Calibrated Beta') }`"
            :disabled="true"
            data-testId="text-field-Calibrated-Beta"
            class="dover-input" />
        </v-flex>
        <v-flex
          xs3
          lg3
          md3>
          <v-btn
            data-testId="btn-Reset"
            small
            class="mx-0 font-weight-light"
            color="dover-blue"
            @click="onCancel()">
            {{ $t('Reset') }}
          </v-btn>
        </v-flex>
        <v-flex
          xs3
          lg10
          md10/>
        <v-expansion-panel
          v-model="calibrationExpanded"
          :disabled="isNewLld">
          <v-expansion-panel-content >
            <template v-slot:header>
              <div>{{ $t('Calibrate') }}</div>
            </template>
            <v-card data-testId="card">
              <v-card-text>
                <v-container
                  fill-height
                  fluid
                  grid-list-xl>
                  <v-layout
                    wrap>
                    <v-flex
                      xs3
                      lg2
                      md2>
                      <v-text-field
                        v-model.number="calibrationSetup.leakGenerator"
                        :disabled="isCalibrationRunning"
                        :rules="rules.required.concat(rules.generator)"
                        :label="`${ $t('Leak Generator (gph)') }`"
                        data-testId="text-field-Leak-Generator"
                        class="dover-input" />
                    </v-flex>
                    <v-flex
                      xs3
                      lg2
                      md2>
                      <v-text-field
                        v-model.number="calibrationSetup.cycles"
                        :disabled="isCalibrationRunning"
                        :counter="2"
                        :rules="rules.required.concat(rules.cycles)"
                        :label="`${ $t('Number of Calibration cycles (3-12)') }`"
                        data-testId="text-field-Number-of-Calibration-cycles"
                        class="dover-input" />
                    </v-flex>
                    <v-flex
                      xs6
                      lg8
                      md8>
                      <v-switch
                        v-model="calibrationSetup.startImmediately"
                        :disabled="isCalibrationRunning"
                        :label="`${ $t('Start calibration immediately') }`"
                        data-testId="switch-Start-calibration-immediately"
                        class="dover-input text-xs-center" />
                    </v-flex>
                  </v-layout>
                </v-container>
                <v-container
                  fill-height
                  fluid
                  grid-list-xl>
                  <v-layout
                    wrap>
                    <v-flex
                      xs6
                      md4>
                      <v-text-field
                        v-model.number="calibrationStatus"
                        :label="`${ $t('Calibration state') }`"
                        data-testId="text-field-Calibration-state"
                        readonly
                        class="dover-input" />
                    </v-flex>
                    <v-flex
                      xs12
                      lg12
                      md12>
                      <MaglinkComponentLoadingBar
                        v-if="isCalibrationRunning"
                        :loading="progressBar.loading"
                        :indeterminate="false"
                        :max-value="progressBar.maxValue"
                        :value="progressBar.currentValue"
                        data-testId="MaglinkComponentLoadingBar" />
                    </v-flex>
                  </v-layout>
                </v-container>
                <v-container
                  fill-height
                  fluid
                  grid-list-xl>
                  <v-layout justify-row-center>
                    <v-flex
                      xs12
                      md12
                      lg12
                      text-xs-center>
                      <v-btn
                        :disabled="!isValidationOk || isCalibrationRunning"
                        data-testId="btn-Start"
                        small
                        class="mx-0 font-weight-light"
                        color="dover-blue"
                        @click="onStart()">
                        {{ $t('Start') }}
                      </v-btn>
                    </v-flex>
                    <v-flex
                      xs12
                      md12
                      lg12
                      text-xs-center>
                      <v-btn
                        :disabled="!isCalibrationRunning"
                        data-testId="btn-Stop"
                        small
                        class="mx-0 font-weight-light"
                        color="dover-blue"
                        @click="onStop()">
                        {{ $t('Stop') }}
                      </v-btn>
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-layout>
    </v-container>
  </v-form>
</template>

<script>
import { i18n } from '@/i18n'
import Vue from 'vue'

export default {
  props: {
    systemSettings: {
      type: Object,
      required: true,
      default: () => {}
    },
    options: {
      type: Object,
      required: true,
      default: () => {}
    },
    isNewLld: {
      type: Boolean,
      require: true,
      default: () => false
    },
    lld: {
      type: Object,
      required: true,
      default: () => {}
    }
  },
  data: () => ({
    isValidationOk: false,
    dialogYN: {
      key: 0,
      show: false,
      componentKey: '',
      title: '',
      labelText: '',
      action: '',
      result: ''
    },
    isCalibrationRunning: false,
    pipeLength: 0.0,
    pipeVolume: 0.0,
    pipeVolumeGallons: 0.0,
    testPressure: 0.0,
    dispensingPressure: 0.0,
    calibrationExpanded: null,
    calibrationSetup: {
      lldNumber: 0,
      leakGenerator: 3.0,
      cycles: 12,
      startImmediately: true
    },
    tickCalibration: 0,
    lldCalibrationStatus: {
      lldNumber: 0,
      currentCycle: 0,
      cycles: 0,
      state: 0,
      lineCalibratedBeta: 0
    },
    progressBar: {
      loading: false,
      maxValue: 12,
      currentValue: 0
    },
    rules: {
      cycles: [ v => Vue.prototype.Maglink.RulesRange(v, 3, 12) ],
      generator: [ v => Vue.prototype.Maglink.RulesRange(v, 1.0, 3.5) ],
      range: [ v => Vue.prototype.Maglink.RulesRange(v, 1000, 50000) ],
      required: [ v => Vue.prototype.Maglink.RulesRequired(v) ],
      span: [ v => Vue.prototype.Maglink.RulesRange(v, 10.00, 100.00) ],
      pipeLength: [ v => Vue.prototype.Maglink.RulesRange(v, 1.0, 100000.0) ],
      pipeVolume: [ v => Vue.prototype.Maglink.RulesRange(v, 0.0001, 10.0) ]
    },
    pipelineVolumes: []
  }),
  computed: {
    calibrationStatus () {
      switch (this.lldCalibrationStatus.state) {
        case 0:
          return !this.isCalibrationRunning ? this.$t('Waiting to start') : '-'
        case 1:
          return this.$t('Calibration done').toUpperCase()
        case 2:
          return this.$t('Calibration in progress')
        case 3:
          return this.$t('Stopped')
        case 4:
          return this.$t('Line error')
        case 5:
          return this.$t('Sensor communication error')
        case 6:
          return this.$t('LIM communication error')
        case 7:
          return this.$t('Communication error')
        case 8:
          return this.$t('LIM error')
        case 9:
          return this.$t('Hook activated')
        case 10:
          return this.$t('Low pressure')
        default:
          return this.$t('Error during calibration')
      }
    }
  },
  watch: {
    lld: {
      handler: function (newLld, oldLld) {
        if (this.lld.minimumTestPressure > 20.00) {
          this.testPressure = this.Maglink.RoundFormat(this.lld.minimumTestPressure, 2)
        } else {
          this.testPressure = 20.00
        }
        if (this.lld.minimumDispensingPressure > 16.00) {
          this.dispensingPressure = this.Maglink.RoundFormat(this.lld.minimumDispensingPressure, 2)
        } else {
          this.dispensingPressure = 16.00
        }
        this.isRunning = (newLld.lineCalibrationProcessStatus === 2)
      },
      immediate: true
    },
    calibrationExpanded: {
      handler: function (newState, oldState) {
        if (this.isExpanded(newState)) {
          if (this.tickCalibration === 0) {
            this.tickCalibration = setInterval(this.refreshLldCalibration, 5000)
          }
          this.refreshLldCalibration()
        } else {
          clearInterval(this.tickCalibration)
          this.tickCalibration = 0
        }
      }
    }
  },
  mounted () {
    this.pipeLength = this.Maglink.LevelToDisplay(this.lld.lineLength, this.systemSettings.levelUnit)
    this.calculateSegmentVolume(false)

    this.refreshLldCalibration()

    this.pipelineVolumes = [
      { value: 0.204, name: this.$t('FIBERGLASS 2.0-INCH  (0.204)') },
      { value: 0.461, name: this.$t('FIBERGLASS 3.0-INCH  (0.461)') },
      { value: 0.190, name: this.$t('STEEL      2.0-INCH  (0.190)') },
      { value: 0.041, name: this.$t('COPPER     1.0-INCH  (0.041)') },
      { value: 0.092, name: this.$t('FLEXIBLE   1.5-INCH  (0.092)') },
      { value: 0.125, name: this.$t('FLEXIBLE   1.75-INCH (0.125)') },
      { value: 0.163, name: this.$t('FLEXIBLE   2.0-INCH  (0.163)') },
      { value: 0.255, name: this.$t('FLEXIBLE   2.5-INCH  (0.255)') },
      { value: 0.367, name: this.$t('FLEXIBLE   3.0-INCH  (0.367)') }
    ]
  },
  beforeDestroy () {
    clearInterval(this.tickCalibration)
    this.tickCalibration = 0
  },
  methods: {
    calculateSegmentVolume (fromDisplay) {
      let levelFactor = this.Maglink.LevelFromDisplay(1, this.systemSettings.levelUnit)
      let volumeFactor = this.Maglink.VolumeFromDisplay(1, this.systemSettings.volumeUnit)

      let totalFactor = volumeFactor / levelFactor
      if (fromDisplay) {
        this.lld.lineSegmentVolume = this.pipeVolume * totalFactor
      } else {
        this.pipeVolume = this.Maglink.RoundFormat(this.lld.lineSegmentVolume / totalFactor, 5)
      }
      this.lineSegmentToGallons()
    },
    lineSegmentToGallons () {
      this.pipeVolumeGallons = this.Maglink.RoundFormat(this.lld.lineSegmentVolume / (3.78541 / 304.8), 5)
    },
    lineSegmentFromGallons () {
      this.lld.lineSegmentVolume = this.pipeVolumeGallons * (3.78541 / 304.8)
    },
    onCancel () {
      let text = this.$t('You are about to RESET the Calibrated Beta')

      this.dialogYN.title = this.$t('Warning').toUpperCase()
      this.dialogYN.labelText = '\n\n' + text + '\n\n' + this.$t('Are you sure?')
      this.dialogYN.componentKey = 'cancel'
      this.dialogYN.action = 'cancel'
      this.dialogYN.result = this.lld.lldNumber.toString()
      this.dialogYN.show = true
    },
    onDialogResult (componentKey, result, action, par2, item) {
      this.dialogYN.show = false

      // NO button pressed
      if (!result) return

      if (componentKey === 'cancel') {
        this.lld.lineCalibratedBeta = 0.00
        this.onSelectionUpdate()
      } else if (componentKey === 'start') {
        this.calibrationSetup.lldNumber = this.lld.lldNumber
        this.Maglink.BetaCalibrationStart(this.calibrationSetup)
          .then(response => {
            this.progressBar.currentValue = 0
            this.progressBar.maxValue = this.calibrationSetup.cycles
            this.isCalibrationRunning = true
            this.refreshLldCalibration()
          })
          .catch(error => (this.snack('error', error.message)))
      } else if (componentKey === 'stop') {
        this.calibrationSetup.lldNumber = this.lld.lldNumber
        this.Maglink.BetaCalibrationStop(this.calibrationSetup)
          .then(response => {
            this.isCalibrationRunning = false
            this.refreshLldCalibration()
          })
          .catch(error => (this.snack('error', error.message)))
      }
    },
    onLineLengthChanged () {
      this.lld.lineLength = this.Maglink.LevelFromDisplay(this.pipeLength, this.systemSettings.levelUnit)
      this.onSelectionUpdate()
    },
    onMinimumDispensingPressureChanged () {
      this.lld.minimumDispensingPressure = this.dispensingPressure
      this.onSelectionUpdate()
    },
    onMinimumTestPressureChanged () {
      this.lld.minimumTestPressure = this.testPressure
      this.onSelectionUpdate()
    },
    onSegmentVolumeChanged () {
      this.calculateSegmentVolume(true)
      this.onSelectionUpdate()
    },
    onSegmentVolumeChangedGallons (item) {
      if (typeof item === 'string') {
        this.pipeVolumeGallons = parseFloat(item)
      } else {
        this.pipeVolumeGallons = item.value
      }
      this.lineSegmentFromGallons()
      this.calculateSegmentVolume(false)
      this.onSelectionUpdate()
    },
    onSelectionUpdate () {
      this.$emit('dataChanged', true)
    },
    onStart () {
      if (this.$refs.lldDetail.validate()) {
        this.isValidationOk = true

        let text = this.$t('Do you want to START Beta Calibration Process?')
        this.dialogYN.title = this.$t('Beta Calibration').toUpperCase()
        this.dialogYN.labelText = text
        this.dialogYN.componentKey = 'start'
        this.dialogYN.action = 'start'
        this.dialogYN.result = this.lld.lldNumber.toString()
        this.dialogYN.show = true
      } else {
        this.isValidationOk = false
      }
    },
    onStop () {
      let text = this.$t('You are about to STOP Beta Calibration Process')

      this.dialogYN.title = this.$t('Warning').toUpperCase()
      this.dialogYN.labelText = '\n\n' + text + '\n\n' + this.$t('Are you sure?')
      this.dialogYN.componentKey = 'stop'
      this.dialogYN.action = 'stop'
      this.dialogYN.result = this.lld.lldNumber.toString()
      this.dialogYN.show = true
    },
    refreshLldCalibration () {
      if (this.lld.lldNumber !== 0) {
        this.Maglink.StatusLldCalibration()
          .then(response => {
            let data = response.data

            for (var i = 0; i < data.length; i++) {
              if (data[i].lldNumber === this.lld.lldNumber) {
                this.lldCalibrationStatus = { ...this.lldCalibrationStatus || {}, ...data[i] }
              }
            }

            if (this.lldCalibrationStatus.state === 2) { // running
              this.isCalibrationRunning = true
            } else {
              if (this.lldCalibrationStatus.state === 1) {
                this.lld.lineCalibratedBeta = this.lldCalibrationStatus.lineCalibratedBeta
              }
              if (this.lldCalibrationStatus.state !== 0) {
                this.isCalibrationRunning = false
              }
            }
            if (this.isCalibrationRunning) {
              this.progressBar.loading = true
              this.progressBar.maxValue = this.lldCalibrationStatus.cycles
              this.progressBar.currentValue = this.lldCalibrationStatus.currentCycle === 0 ? 0.2 : this.lldCalibrationStatus.currentCycle
            } else {
              this.progressBar.loading = false
            }
          })
          .catch(error => (this.snack('error', error.message)))
      }
    },
    isExpanded (state) {
      return (state !== null && state === 0)
    },
    snack (color, message) {
      message = i18n.t('Line leak config') + ': ' + message
      this.$store.dispatch('showSnackbar', { color, message, show: true })
    }
  }
}
</script>
